import React from 'react';
import { BodyText } from '@chargify/custom-ui-components';

import { SubscriptionStatus } from '../../models';
import { FormattedSubscriptionDetails } from '../../hooks';

import {
  Active,
  Canceled,
  CancellationScheduled,
  Expired,
  OnHold,
  PastDue,
  PlanChangeScheduled,
  Trial,
  TrialExpired,
  Unpaid,
} from './components';

export type Props = Partial<FormattedSubscriptionDetails> & {
  subscriptionStatus: SubscriptionStatus;
};

const renderDetails = ({
  subscriptionStatus,
  nextBillingAmount,
  nextBillingDate,
  basePlanPrice,
  cancellationDate,
  expirationDate,
  autoResumeDate,
  trialExpirationDate,
  priceAfterTrial,
  trialPrice,
  currentBalance,
  nextBillingAttempt,
  scheduledCancellationDate,
}: Props) => {
  switch (subscriptionStatus) {
    case 'active':
      return (
        <Active
          basePlanPrice={basePlanPrice}
          nextBillingAmount={nextBillingAmount}
          nextBillingDate={nextBillingDate}
        />
      );
    case 'past_due':
      return (
        <PastDue
          basePlanPrice={basePlanPrice}
          currentBalance={currentBalance}
          nextBillingAttempt={nextBillingAttempt}
        />
      );
    case 'expired':
      return (
        <Expired
          expirationDate={expirationDate}
          basePlanPrice={basePlanPrice}
        />
      );
    case 'canceled':
      return (
        <Canceled
          basePrice={basePlanPrice}
          cancellationDate={cancellationDate}
        />
      );
    case 'trial':
      return (
        <Trial
          trialExpirationDate={trialExpirationDate}
          nextBillingDate={nextBillingDate}
          priceAfterTrial={priceAfterTrial}
          trialPrice={trialPrice}
        />
      );
    case 'trial_expired':
      return <TrialExpired trialExpirationDate={trialExpirationDate} />;
    case 'cancellation_scheduled':
      return (
        <CancellationScheduled
          scheduledCancellationDate={scheduledCancellationDate}
          basePlanPrice={basePlanPrice}
          nextBillingAmount={nextBillingAmount}
          nextBillingDate={nextBillingDate}
        />
      );
    case 'on_hold':
      return (
        <OnHold basePlanPrice={basePlanPrice} autoResumeDate={autoResumeDate} />
      );
    case 'unpaid':
      return (
        <Unpaid basePlanPrice={basePlanPrice} currentBalance={currentBalance} />
      );
    case 'plan_change_scheduled':
      return (
        <PlanChangeScheduled
          basePlanPrice={basePlanPrice}
          nextBillingAmount={nextBillingAmount}
          nextBillingDate={nextBillingDate}
        />
      );
    default:
      return <BodyText>Status not recognized: {subscriptionStatus}</BodyText>;
  }
};

const SubscriptionDetails = ({
  subscriptionStatus,
  nextBillingAmount,
  nextBillingDate,
  basePlanPrice,
  cancellationDate,
  expirationDate,
  trialExpirationDate,
  priceAfterTrial,
  trialPrice,
  currentBalance,
  nextBillingAttempt,
  autoResumeDate,
  scheduledCancellationDate,
}: Props) => {
  return renderDetails({
    scheduledCancellationDate,
    subscriptionStatus,
    nextBillingAmount,
    nextBillingDate,
    basePlanPrice,
    cancellationDate,
    expirationDate,
    autoResumeDate,
    trialExpirationDate,
    priceAfterTrial,
    trialPrice,
    currentBalance,
    nextBillingAttempt,
  });
};

export default SubscriptionDetails;
